// MIT तंत्रप्रशाला — 3-page Marathi school website (Saffron + Maroon + Cream)
import React, { useEffect, useState, useMemo, createContext, useContext } from "react";
import { NavLink, Outlet, useLocation, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import InstituteSeo from "@/components/InstituteSeo";
import api from "@/lib/api";
import {
  Phone, Mail, MapPin, Menu, X, ArrowRight, GraduationCap, ShieldCheck,
  Bus, BookOpen, Cpu, Cog, Bot, Trophy, FlaskConical, Beaker, Users, Award,
  ExternalLink, Facebook, Instagram, Linkedin, Youtube, FileText, Send, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import InstituteSwitcher from "@/components/InstituteSwitcher";
import GoverningBodySection from "@/components/GoverningBodySection";
import PublicAcademicsPage from "@/components/PublicAcademicsPage";
import RecentAcademicsWidget from "@/components/RecentAcademicsWidget";
import AlumniStrip from "@/components/AlumniStrip";

const SLUG = "mitschool";
const Ctx = createContext(null);
export const useSchool = () => useContext(Ctx);

const NAV = [
  { to: "", end: true, label: "मुख्यपृष्ठ" },
  { to: "about", label: "आमच्याबद्दल" },
  { to: "gallery", label: "गॅलरी" },
  { to: "academics", label: "शैक्षणिक" },
  { to: "alumni", label: "माजी विद्यार्थी" },
  { to: "contact", label: "संपर्क" },
];

// --- Render HTML helper (supports plain Marathi text or rich HTML)
function renderHtml(content) {
  if (!content) return null;
  const isHtml = /<\/?[a-z][\s\S]*?>/i.test(content);
  return isHtml ? content : content.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, "<br/>");
}

export default function SchoolLayout() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get(`/public/website/${SLUG}`);
        if (!cancel) setData(data);
      } catch (e) {
        if (!cancel) setErr(e?.response?.data?.detail || "वेबसाइट कॉन्फिगर केलेली नाही");
      } finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, []);

  useEffect(() => { window.scrollTo({ top: 0 }); setMobileOpen(false); }, [location.pathname]);

  if (loading) return <div className="min-h-screen bg-[#FFF8EC] flex items-center justify-center text-[#8B1A1A] font-['Noto_Sans_Devanagari',sans-serif]"><GraduationCap size={40} className="animate-pulse" /></div>;
  if (err || !data) return <div className="min-h-screen bg-[#FFF8EC] flex items-center justify-center px-4 font-['Noto_Sans_Devanagari',sans-serif] text-center"><div><GraduationCap size={36} className="mx-auto text-[#8B1A1A]" /><h1 className="mt-4 text-2xl font-bold text-[#1c1a17]">वेबसाइट उपलब्ध नाही</h1><p className="mt-2 text-sm text-[#475569]">{err || "कृपया नंतर पुन्हा प्रयत्न करा."}</p></div></div>;

  const inst = data.institute || {};
  const logo = inst.logo_url || "/assets/mitcsn-logo.png";

  return (
    <Ctx.Provider value={data}>
      <InstituteSeo slug="mitschool" inst={inst} themeColor="#8B1A1A" ogImage="https://gsmandal.org/assets/og/mit-school-og.jpg" />
      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Noto+Sans+Devanagari:wght@400;500;600;700;800&family=Tiro+Devanagari+Marathi&display=swap" />
      </Helmet>

      <div className="min-h-screen flex flex-col bg-[#FFF8EC] text-[#1c1a17]" style={{ fontFamily: "'Noto Sans Devanagari', sans-serif" }}>
        {/* Top utility */}
        <div className="bg-[#8B1A1A] text-white/90 text-[12px]">
          <div className="max-w-7xl mx-auto px-4 py-2 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-4">
              {inst.email && <a href={`mailto:${inst.email}`} className="inline-flex items-center gap-1 hover:text-[#FFC78A]" data-testid="sch-top-email"><Mail size={11} />{inst.email}</a>}
              {inst.city && <span className="inline-flex items-center gap-1"><MapPin size={11} className="text-[#FFC78A]" />{inst.address1 || "सातारा परिसर"}, {inst.city}</span>}
            </div>
            <div className="flex items-center gap-3">
              <InstituteSwitcher currentSlug="mitschool" testidPrefix="sch-top-switcher" />
              {inst.facebook_url && <a href={inst.facebook_url} target="_blank" rel="noopener noreferrer" className="hover:text-[#FFC78A]"><Facebook size={12} /></a>}
              {inst.instagram_url && <a href={inst.instagram_url} target="_blank" rel="noopener noreferrer" className="hover:text-[#FFC78A]"><Instagram size={12} /></a>}
              {inst.linkedin_url && <a href={inst.linkedin_url} target="_blank" rel="noopener noreferrer" className="hover:text-[#FFC78A]"><Linkedin size={12} /></a>}
              {inst.youtube_url && <a href={inst.youtube_url} target="_blank" rel="noopener noreferrer" className="hover:text-[#FFC78A]"><Youtube size={12} /></a>}
              <span className="hidden sm:inline-block bg-[#FF7B00] px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold">इयत्ता ८ वी व ९ वी प्रवेश सुरू</span>
            </div>
          </div>
        </div>

        {/* Header band */}
        <header className="sticky top-0 z-40 bg-[#FFF8EC]/95 backdrop-blur border-b border-[#E8DCC0]">
          <div className="max-w-7xl mx-auto px-4 py-3 grid grid-cols-[auto_1fr_auto] items-center gap-4">
            <NavLink to="" end className="flex items-center" data-testid="sch-logo">
              <img src={logo} alt={inst.name} className="h-[96px] sm:h-[112px] w-auto object-contain" />
            </NavLink>
            <div className="text-center leading-tight">
              <div className="text-[10px] sm:text-[11px] tracking-[0.18em] text-[#8B1A1A] font-semibold" data-testid="sch-trust">
                ग्रा. शि. मंडळ, छत्रपती संभाजीनगर संचालित
              </div>
              <div className="text-xl sm:text-3xl text-[#8B1A1A] font-extrabold mt-1" style={{ fontFamily: "'Tiro Devanagari Marathi', 'Noto Sans Devanagari', serif" }} data-testid="sch-name">
                {inst.name || "एम. आय. टी. तंत्रप्रशाला"}
              </div>
              <div className="text-[10px] sm:text-xs text-[#FF7B00] italic mt-1 tracking-wider">शासन मान्य अनुदानित संस्था · {inst.year_of_establishment || "१९७९"} पासून सेवा</div>
            </div>
            <Link to="contact" className="hidden sm:inline-flex bg-[#FF7B00] hover:bg-[#E36B00] text-white font-bold text-xs uppercase tracking-wider px-4 py-2 rounded-md shadow-sm">आजच प्रवेश घ्या</Link>
          </div>

          {/* Main nav */}
          <nav className="bg-[#8B1A1A] shadow-md" data-testid="sch-nav">
            <div className="max-w-7xl mx-auto px-4">
              <div className="hidden md:flex items-center gap-1 py-2 flex-wrap">
                {NAV.map((n) => (
                  <NavLink key={n.to} to={n.to} end={n.end} data-testid={`sch-nav-${n.to || "home"}`}
                    className={({ isActive }) => `text-sm px-4 py-2 rounded-md transition-colors font-semibold ${isActive ? "bg-[#FFC78A] text-[#8B1A1A]" : "text-white/95 hover:bg-white/10"}`}>
                    {n.label}
                  </NavLink>
                ))}
              </div>
              <div className="md:hidden flex items-center justify-between py-2">
                <span className="text-white font-semibold text-sm">मेन्यू</span>
                <button onClick={() => setMobileOpen((v) => !v)} className="p-2 text-white" aria-label="Toggle menu" data-testid="sch-nav-toggle">{mobileOpen ? <X size={20} /> : <Menu size={20} />}</button>
              </div>
              {mobileOpen && (
                <div className="md:hidden grid grid-cols-1 gap-1 pb-3">
                  {NAV.map((n) => (
                    <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setMobileOpen(false)}
                      className={({ isActive }) => `text-sm px-3 py-2 rounded-md ${isActive ? "bg-[#FFC78A] text-[#8B1A1A]" : "text-white/95 hover:bg-white/10"}`}>{n.label}</NavLink>
                  ))}
                </div>
              )}
            </div>
          </nav>
        </header>

        {/* Scrolling announcement bar */}
        {Array.isArray(inst.scrollers) && inst.scrollers.length > 0 && (
          <SchoolScrollerBar items={inst.scrollers} />
        )}

        <main className="flex-1"><Outlet /></main>

        {/* Footer */}
        <footer className="bg-[#8B1A1A] text-white/85 mt-12">
          <div className="max-w-7xl mx-auto px-4 py-10 grid md:grid-cols-3 gap-8">
            <div>
              <div className="text-[#FFC78A] uppercase tracking-[0.2em] text-xs font-bold">{inst.name || "एम. आय. टी. तंत्रप्रशाला"}</div>
              <p className="mt-3 text-sm">ग्रामोद्योगिक शिक्षण मंडळ संचालित — कौशल्याधारित तांत्रिक शिक्षणाची भक्कम पायाभरणी.</p>
            </div>
            <div>
              <div className="text-white font-bold mb-3 text-sm">द्रुत दुवे</div>
              <ul className="space-y-2 text-sm">
                {NAV.map((n) => <li key={n.to}><NavLink to={n.to} end={n.end} className="hover:text-[#FFC78A]">{n.label}</NavLink></li>)}
              </ul>
            </div>
            <div>
              <div className="text-white font-bold mb-3 text-sm">संपर्क</div>
              <ul className="space-y-2 text-sm">
                {(inst.address1 || inst.city) && <li className="flex items-start gap-2"><MapPin size={13} className="mt-0.5 text-[#FFC78A] shrink-0" />{[inst.address1, inst.city, inst.pincode && `– ${inst.pincode}`].filter(Boolean).join(", ")}</li>}
                {inst.email && <li className="flex items-start gap-2"><Mail size={13} className="mt-0.5 text-[#FFC78A]" /><a href={`mailto:${inst.email}`} className="hover:text-[#FFC78A]">{inst.email}</a></li>}
                {(inst.contact_phone || inst.admission_phone) && <li className="flex items-start gap-2"><Phone size={13} className="mt-0.5 text-[#FFC78A]" />{[inst.admission_phone, inst.contact_phone].filter(Boolean).join(" · ")}</li>}
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 py-4 text-center text-[11px] text-white/70">© {new Date().getFullYear()} {inst.name} · ग्रामोद्योगिक शिक्षण मंडळ. सर्व हक्क राखीव.</div>
        </footer>
      </div>
    </Ctx.Provider>
  );
}

// ============================================================
//  HOME PAGE
// ============================================================
export function SchoolHome() {
  const data = useSchool();
  const inst = data.institute || {};
  const depts = data.departments || [];
  const staff = (data.staff || []).slice(0, 5);
  const aboutHtml = renderHtml((inst.about_institute || "").trim() || "एम. आय. टी. तंत्रप्रशाला विद्यार्थ्यांना शालेय शिक्षणासोबत इलेक्ट्रिकल, इंजिनिअरिंग ड्रॉईंग, रोबोटिक्स, संगणक शिक्षण आणि कौशल्य विकासाचे प्रशिक्षण देते.");

  const stats = [
    { v: "१००%", l: "निःशुल्क प्रवेश", icon: ShieldCheck },
    { v: "६०+६०", l: "प्रवेश क्षमता", icon: Users },
    { v: inst.year_of_establishment || "१९७९", l: "पासून सेवा", icon: Award },
  ];

  const usp = [
    { icon: Cog, t: "तांत्रिक शिक्षण", d: "इलेक्ट्रिकल आणि इंजिनिअरिंग ड्रॉईंग सारखे उद्योगाभिमुख विषय." },
    { icon: Bot, t: "रोबोटिक्स प्रशिक्षण", d: "STEM, Coding Basics, Mini Projects आणि Design Thinking." },
    { icon: Cpu, t: "संगणक शिक्षण", d: "डिजिटल कौशल्ये आणि भविष्यकालीन तंत्रज्ञानाची ओळख." },
    { icon: Bus, t: "मोफत सुविधा", d: "मोफत बस, पाठ्यपुस्तके, मध्यान्ह भोजन आणि मार्गदर्शन." },
  ];

  const facilities = [
    "मोफत प्रवेश, बस सुविधा, पाठ्यपुस्तके व मध्यान्ह भोजन",
    "आधुनिक विज्ञान प्रयोगशाळा व अत्याधुनिक विद्युत प्रयोगशाळा",
    "डिजिटल शिक्षण, संगणक व रोबोटिक्स प्रशिक्षण",
    "सुसज्ज क्रीडांगण व स्वतंत्र स्वच्छतागृहे",
    "करिअर मार्गदर्शन, समुपदेशन व स्पर्धापरीक्षा मार्गदर्शन",
    "अनुभवी व प्रशिक्षित शिक्षकवृंद",
  ];

  return (
    <div data-testid="sch-home">
      {/* HERO */}
      <section className="relative bg-gradient-to-br from-[#FFF8EC] via-[#FFE8C8] to-[#FFD9A8] overflow-hidden">
        <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-[#FF7B00]/20 blur-3xl" />
        <div className="absolute -bottom-20 -left-20 w-96 h-96 rounded-full bg-[#8B1A1A]/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 py-14 lg:py-20 grid lg:grid-cols-[1.1fr_1fr] gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-white text-[#8B1A1A] border border-[#FFC78A] px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider"><GraduationCap size={14} /> इयत्ता ८ वी व ९ वी साठी प्रवेश सुरू</div>
            <h1 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight text-[#8B1A1A]" style={{ fontFamily: "'Tiro Devanagari Marathi', 'Noto Sans Devanagari', serif" }}>
              कौशल्याधारित तांत्रिक शिक्षणाची <span className="text-[#FF7B00]">भक्कम पायाभरणी</span>
            </h1>
            <div className="mt-5 text-[#5b4a35] leading-relaxed prose prose-sm max-w-xl" dangerouslySetInnerHTML={{ __html: aboutHtml }} />
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="contact" className="inline-flex items-center gap-2 bg-[#FF7B00] hover:bg-[#E36B00] text-white font-bold px-6 py-3 rounded-md shadow-md">आजच प्रवेश घ्या <ArrowRight size={16} /></Link>
              <Link to="about" className="inline-flex items-center gap-2 border-2 border-[#8B1A1A] text-[#8B1A1A] hover:bg-[#8B1A1A] hover:text-white font-bold px-6 py-3 rounded-md transition-colors">अभ्यासक्रम पहा</Link>
            </div>
            <div className="mt-8 grid grid-cols-3 gap-4">
              {stats.map((s) => {
                const Icon = s.icon;
                return (
                  <div key={s.l} className="bg-white border border-[#F0E2C0] rounded-xl p-3 sm:p-4 flex items-center gap-2">
                    <Icon size={20} className="text-[#FF7B00] shrink-0" />
                    <div className="min-w-0"><div className="font-extrabold text-[#8B1A1A] text-lg leading-tight">{s.v}</div><div className="text-[11px] text-[#5b4a35] leading-tight">{s.l}</div></div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="relative">
            {(data.header_slides || [])[0]?.image_url ? (
              <img src={data.header_slides[0].image_url} alt={inst.name} className="w-full aspect-[5/4] object-cover rounded-2xl shadow-2xl ring-1 ring-[#F0E2C0]" />
            ) : inst.home_image_url ? (
              <img src={inst.home_image_url} alt={inst.name} className="w-full aspect-[5/4] object-cover rounded-2xl shadow-2xl ring-1 ring-[#F0E2C0]" />
            ) : (
              <div className="w-full aspect-[5/4] rounded-2xl shadow-2xl bg-gradient-to-br from-[#8B1A1A] to-[#FF7B00] flex items-center justify-center">
                <GraduationCap size={120} className="text-white/30" strokeWidth={1} />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="py-14 bg-white" data-testid="sch-why">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-2xl sm:text-4xl font-extrabold text-[#8B1A1A]" style={{ fontFamily: "'Tiro Devanagari Marathi', 'Noto Sans Devanagari', serif" }}>का निवडावी MIT तंत्रप्रशाला?</h2>
          <p className="mt-3 text-[#5b4a35] max-w-2xl mx-auto">विद्यार्थ्यांना रोजगारक्षम, कौशल्यसंपन्न आणि आत्मनिर्भर बनविण्यासाठी आधुनिक तंत्रज्ञान व प्रात्यक्षिक शिक्षणावर भर.</p>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {usp.map((u) => {
              const Icon = u.icon;
              return (
                <div key={u.t} className="bg-[#FFF8EC] border-2 border-[#F0E2C0] hover:border-[#FF7B00] hover:shadow-lg rounded-2xl p-6 text-center transition-all">
                  <span className="inline-flex w-14 h-14 rounded-full bg-[#FF7B00] text-white items-center justify-center"><Icon size={24} /></span>
                  <h3 className="mt-3 font-bold text-[#8B1A1A] text-lg">{u.t}</h3>
                  <p className="mt-2 text-sm text-[#5b4a35] leading-relaxed">{u.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* COURSES */}
      <section className="py-14 bg-[#FFF8EC]" data-testid="sch-courses">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-4xl font-extrabold text-[#8B1A1A]" style={{ fontFamily: "'Tiro Devanagari Marathi', 'Noto Sans Devanagari', serif" }}>अभ्यासक्रम व विशेष प्रशिक्षण</h2>
            <p className="mt-2 text-[#5b4a35]">शालेय विषयांसोबत प्रत्यक्ष कौशल्याधारित प्रशिक्षण.</p>
          </div>
          {depts.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {depts.map((d, i) => {
                const iconList = [Cog, BookOpen, Bot, GraduationCap];
                const Icon = iconList[i % iconList.length];
                return (
                  <div key={d.id} data-testid={`sch-course-${d.id}`} className="bg-white border-l-4 border-[#FF7B00] rounded-r-xl p-5 shadow-sm hover:shadow-md transition-shadow">
                    <Icon size={22} className="text-[#8B1A1A]" />
                    <h3 className="mt-2 font-bold text-[#8B1A1A]">{d.name}</h3>
                    {d.code && <div className="mt-1 text-[10px] uppercase tracking-wider text-[#5b4a35]">{d.code}</div>}
                    {d.about_html && <div className="mt-2 text-xs text-[#5b4a35] prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: d.about_html }} />}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                { t: "तांत्रिक विषय", items: ["इलेक्ट्रिकल", "इंजिनिअरिंग ड्रॉईंग"], Icon: Cog },
                { t: "शालेय विषय", items: ["मराठी, हिंदी, इंग्रजी", "गणित, विज्ञान व तंत्रज्ञान", "सामाजिक शास्त्रे"], Icon: BookOpen },
                { t: "विशेष प्रशिक्षण", items: ["रोबोटिक्स", "संगणक शिक्षण", "व्यक्तिमत्त्व विकास"], Icon: Bot },
                { t: "करिअर संधी", items: ["ITI, MCVC, Polytechnic आरक्षण", "इलेक्ट्रिशियन, वायरमन", "तंत्रज्ञ, अभियंता, उद्योजक"], Icon: GraduationCap },
              ].map((c) => (
                <div key={c.t} className="bg-white border-l-4 border-[#FF7B00] rounded-r-xl p-5 shadow-sm">
                  <c.Icon size={22} className="text-[#8B1A1A]" />
                  <h3 className="mt-2 font-bold text-[#8B1A1A]">{c.t}</h3>
                  <ul className="mt-3 text-sm text-[#5b4a35] space-y-1.5">{c.items.map((i) => <li key={i}>• {i}</li>)}</ul>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ADMISSION */}
      <section className="py-14 bg-white" data-testid="sch-admission">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="text-2xl sm:text-4xl font-extrabold text-[#8B1A1A]" style={{ fontFamily: "'Tiro Devanagari Marathi', 'Noto Sans Devanagari', serif" }}>प्रवेश प्रक्रिया</h2>
            <p className="mt-3 text-[#5b4a35]">इयत्ता ७ वी उत्तीर्ण विद्यार्थ्यांना गुणवत्ता यादी नुसार इयत्ता ८ वी व ९ वी मध्ये प्रवेश.</p>
            <div className="mt-5 flex flex-wrap gap-2">
              {["१००% निःशुल्क प्रवेश", "तांत्रिक शिक्षण", "कौशल्य विकास", "आधुनिक प्रयोगशाळा"].map((t) => (
                <span key={t} className="inline-block bg-[#FFC78A] text-[#8B1A1A] text-xs font-bold px-3 py-1.5 rounded-full">{t}</span>
              ))}
            </div>
          </div>
          <div className="bg-[#FFF8EC] rounded-xl border-2 border-[#F0E2C0] overflow-hidden">
            <div className="bg-[#8B1A1A] text-white px-5 py-3 font-bold">प्रवेश क्षमता</div>
            <table className="w-full text-sm">
              <thead className="bg-[#FFE8C8]"><tr><th className="text-left py-3 px-5 font-bold text-[#8B1A1A]">इयत्ता</th><th className="text-left py-3 px-5 font-bold text-[#8B1A1A]">प्रवेश क्षमता</th></tr></thead>
              <tbody>
                <tr className="border-t border-[#F0E2C0]"><td className="py-3 px-5">८ वी</td><td className="py-3 px-5 font-bold text-[#FF7B00]">६०</td></tr>
                <tr className="border-t border-[#F0E2C0]"><td className="py-3 px-5">९ वी</td><td className="py-3 px-5 font-bold text-[#FF7B00]">६०</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FACILITIES */}
      <section className="py-14 bg-[#FFF8EC]" data-testid="sch-facilities">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-center text-2xl sm:text-4xl font-extrabold text-[#8B1A1A]" style={{ fontFamily: "'Tiro Devanagari Marathi', 'Noto Sans Devanagari', serif" }}>विद्यार्थ्यांसाठी सुविधा</h2>
          <p className="mt-2 text-center text-[#5b4a35]">विद्यार्थी-केंद्रित शिक्षण व्यवस्थेसाठी आवश्यक सुविधा.</p>
          <div className="mt-8 grid sm:grid-cols-2 gap-4 max-w-4xl mx-auto">
            {facilities.map((f) => (
              <div key={f} className="bg-white border border-[#F0E2C0] rounded-lg p-4 flex items-start gap-3"><ShieldCheck size={18} className="text-[#FF7B00] shrink-0 mt-0.5" /><span className="text-sm text-[#1c1a17]">{f}</span></div>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY section moved to dedicated /mitschool/gallery page */}

      {/* TEACHERS */}
      {staff.length > 0 && (
        <section className="py-14 bg-[#FFF8EC]" data-testid="sch-teachers">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-center text-2xl sm:text-4xl font-extrabold text-[#8B1A1A]" style={{ fontFamily: "'Tiro Devanagari Marathi', 'Noto Sans Devanagari', serif" }}>शिक्षकवृंद</h2>
            <p className="mt-2 text-center text-[#5b4a35]">अनुभवी शिक्षकांचे मार्गदर्शन आणि विद्यार्थी-केंद्रित शिक्षण.</p>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {staff.map((s) => (
                <div key={s.id} className="bg-white rounded-xl p-4 ring-1 ring-[#F0E2C0] text-center hover:shadow-md transition-shadow">
                  {s.photo_url ? <img src={s.photo_url} alt={s.name} className="w-20 h-20 rounded-full object-cover mx-auto ring-2 ring-[#FFC78A]" /> : <div className="w-20 h-20 rounded-full bg-[#FFC78A] text-[#8B1A1A] mx-auto flex items-center justify-center"><Users size={28} /></div>}
                  <div className="mt-3 font-bold text-[#8B1A1A] text-sm">{s.name}</div>
                  {s.designation && <div className="text-[11px] text-[#5b4a35]">{s.designation}</div>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Strip */}
      <section className="py-12 bg-gradient-to-r from-[#8B1A1A] to-[#A52A2A] text-white text-center">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-2xl sm:text-4xl font-extrabold" style={{ fontFamily: "'Tiro Devanagari Marathi', 'Noto Sans Devanagari', serif" }}>आजच प्रवेश घ्या!</h2>
          <p className="mt-3 text-[#FFC78A] italic">"तांत्रिक शिक्षणाची भक्कम पायाभरणी – उज्ज्वल भविष्यासाठी खात्रीशीर वाटचाल!"</p>
          <Link to="contact" className="mt-6 inline-flex items-center gap-2 bg-[#FF7B00] hover:bg-[#E36B00] text-white font-bold px-7 py-3 rounded-md shadow-lg">प्रवेशासाठी संपर्क करा <ArrowRight size={16} /></Link>
        </div>
      </section>
      <RecentAcademicsWidget docs={data.academics || []} theme="saffron" testidPrefix="sch-ac" linkTo="academics" eyebrow="शैक्षणिक" heading="अलीकडील दस्तऐवज" />
      <AlumniStrip items={(data.alumni || []).filter((a) => a.featured)} theme="school" eyebrow="माजी विद्यार्थी" heading="आमचे आजचे माजी विद्यार्थी" testid="sch-alumni" registerHref="/mitschool/alumni/register" />
    </div>
  );
}

// ============================================================
//  ABOUT PAGE
// ============================================================
export function SchoolAbout() {
  const data = useSchool();
  const inst = data.institute || {};
  const leaders = data.leadership || [];
  const visionHtml = renderHtml((inst.vision || "").trim() || "विद्यार्थ्यांना कौशल्यसंपन्न आणि आत्मनिर्भर बनविणारी एक आदर्श तांत्रिक शाळा.");
  const missionHtml = renderHtml((inst.mission || "").trim() || "उद्योगाभिमुख तांत्रिक शिक्षण, आधुनिक प्रात्यक्षिक, रोबोटिक्स व संगणक प्रशिक्षण देऊन प्रत्येक विद्यार्थ्यास रोजगारक्षम बनविणे.");
  const aboutHtml = renderHtml((inst.about_institute || "").trim() || "एम. आय. टी. तंत्रप्रशाला ही ग्रामोद्योगिक शिक्षण मंडळ संचालित शासन मान्य अनुदानित संस्था असून १९७९ पासून छत्रपती संभाजीनगर येथील सातारा परिसरात कार्यरत आहे.");

  return (
    <div data-testid="sch-about">
      <section className="bg-gradient-to-br from-[#8B1A1A] to-[#A52A2A] text-white py-14">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#FFC78A] font-bold">आमची ओळख</div>
          <h1 className="mt-2 text-3xl sm:text-5xl font-extrabold" style={{ fontFamily: "'Tiro Devanagari Marathi', 'Noto Sans Devanagari', serif" }}>आमच्याबद्दल</h1>
          <p className="mt-4 max-w-2xl text-white/85">ग्रामोद्योगिक शिक्षण मंडळ संचालित — कौशल्याधारित तांत्रिक शिक्षणाची भक्कम पायाभरणी, १९७९ पासून.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-12 grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 bg-white rounded-2xl border border-[#F0E2C0] p-7 shadow-sm">
          <h2 className="text-2xl font-extrabold text-[#8B1A1A]" style={{ fontFamily: "'Tiro Devanagari Marathi', 'Noto Sans Devanagari', serif" }}>शाळेबद्दल</h2>
          <div className="mt-4 prose prose-sm max-w-none text-[#1c1a17] leading-relaxed" data-testid="sch-about-text" dangerouslySetInnerHTML={{ __html: aboutHtml }} />
        </div>
        <div className="space-y-4">
          <div className="bg-gradient-to-br from-[#8B1A1A] to-[#a02828] rounded-2xl p-6 text-white shadow-md">
            <h3 className="text-lg font-extrabold" style={{ fontFamily: "'Tiro Devanagari Marathi', 'Noto Sans Devanagari', serif" }}>आमची दृष्टी</h3>
            <div className="mt-2 text-sm prose prose-invert prose-sm max-w-none" data-testid="sch-vision-text" dangerouslySetInnerHTML={{ __html: visionHtml }} />
          </div>
          <div className="bg-gradient-to-br from-[#FF7B00] to-[#E36B00] rounded-2xl p-6 text-white shadow-md">
            <h3 className="text-lg font-extrabold" style={{ fontFamily: "'Tiro Devanagari Marathi', 'Noto Sans Devanagari', serif" }}>आमचे ध्येय</h3>
            <div className="mt-2 text-sm prose prose-invert prose-sm max-w-none" data-testid="sch-mission-text" dangerouslySetInnerHTML={{ __html: missionHtml }} />
          </div>
        </div>
      </section>

      {leaders.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 pb-14">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-[#8B1A1A] mb-6" style={{ fontFamily: "'Tiro Devanagari Marathi', 'Noto Sans Devanagari', serif" }}>नेतृत्वाचा संदेश</h2>
          <div className="grid md:grid-cols-2 gap-5">
            {leaders.map((l) => (
              <div key={l.id} className="bg-white border border-[#F0E2C0] rounded-2xl p-6 flex gap-5 items-start" data-testid={`sch-leader-${l.id}`}>
                {l.photo_url ? <img src={l.photo_url} alt={l.name} className="w-24 h-24 rounded-full object-cover ring-4 ring-[#FFC78A] shrink-0" /> : <div className="w-24 h-24 rounded-full bg-[#FFC78A] text-[#8B1A1A] flex items-center justify-center shrink-0"><Users size={32} /></div>}
                <div className="min-w-0 flex-1">
                  <div className="font-extrabold text-[#8B1A1A] text-lg">{l.name}</div>
                  {l.designation && <div className="text-xs uppercase tracking-wider text-[#FF7B00] font-bold">{l.designation}</div>}
                  {l.message_html && (
                    <div
                      className="mt-3 text-sm text-[#5b4a35] leading-relaxed prose prose-sm max-w-none"
                      data-testid={`sch-leader-msg-${l.id}`}
                      dangerouslySetInnerHTML={{ __html: renderHtml(l.message_html) }}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-6 pb-14">
        <GoverningBodySection
          members={data.governing_body || []}
          theme="saffron"
          testidPrefix="sch-gb"
          heading="संचालक मंडळ"
          eyebrow="Governing Body"
        />
      </section>
    </div>
  );
}

// ============================================================
//  GALLERY PAGE (कॅम्पस गॅलरी)
// ============================================================
export function SchoolGallery() {
  const data = useSchool();
  const photos = data.gallery || [];
  const [lb, setLb] = useState(-1);

  const types = useMemo(() => {
    const set = new Set();
    photos.forEach((p) => p.gallery_type && set.add(p.gallery_type));
    return Array.from(set);
  }, [photos]);
  const [filter, setFilter] = useState("");
  const visible = filter ? photos.filter((p) => p.gallery_type === filter) : photos;

  return (
    <div data-testid="sch-gallery">
      <section className="bg-gradient-to-br from-[#8B1A1A] to-[#A52A2A] text-white py-14">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#FFC78A] font-bold">क्षणचित्रे</div>
          <h1 className="mt-2 text-3xl sm:text-5xl font-extrabold" style={{ fontFamily: "'Tiro Devanagari Marathi', 'Noto Sans Devanagari', serif" }}>कॅम्पस गॅलरी</h1>
          <p className="mt-4 max-w-2xl text-white/85">शाळा इमारत, लॅब, रोबोटिक्स प्रशिक्षण, क्रीडांगण, उपक्रम आणि औद्योगिक भेटी.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-10">
        {types.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-6" data-testid="sch-gallery-filters">
            <button
              type="button"
              onClick={() => { setFilter(""); setLb(-1); }}
              data-testid="sch-gallery-filter-all"
              className={`text-sm px-4 py-2 rounded-full transition-colors ${
                filter === ""
                  ? "bg-[#8B1A1A] text-white font-semibold shadow-sm"
                  : "bg-white text-[#8B1A1A] ring-1 ring-[#F0E2C0] hover:bg-[#FFF1DC]"
              }`}
            >
              सर्व ({photos.length})
            </button>
            {types.map((t) => {
              const count = photos.filter((p) => p.gallery_type === t).length;
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => { setFilter(t); setLb(-1); }}
                  data-testid={`sch-gallery-filter-${t.replace(/\s+/g, "-").toLowerCase()}`}
                  className={`text-sm px-4 py-2 rounded-full transition-colors ${
                    filter === t
                      ? "bg-[#FF7B00] text-white font-semibold shadow-sm"
                      : "bg-white text-[#8B1A1A] ring-1 ring-[#F0E2C0] hover:bg-[#FFF1DC]"
                  }`}
                >
                  {t} ({count})
                </button>
              );
            })}
          </div>
        )}

        {visible.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center ring-1 ring-[#F0E2C0]" data-testid="sch-gallery-empty">
            <FileText size={36} strokeWidth={1.2} className="mx-auto text-[#8B1A1A]/70" />
            <div className="mt-3 text-sm text-[#5b4a35]">
              {filter ? `"${filter}" मध्ये अद्याप छायाचित्रे नाहीत.` : "लवकरच छायाचित्रे प्रकाशित केली जातील."}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="sch-gallery-grid">
            {visible.map((g, i) => (
              <button
                key={g.id}
                onClick={() => setLb(i)}
                data-testid={`sch-gallery-photo-${g.id}`}
                className="aspect-square overflow-hidden rounded-xl ring-1 ring-[#F0E2C0] hover:ring-2 hover:ring-[#FF7B00] transition group"
              >
                <img src={g.image_url} alt={g.caption || ""} loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
              </button>
            ))}
          </div>
        )}

        {lb >= 0 && visible[lb] && (
          <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setLb(-1)} data-testid="sch-gallery-lightbox">
            <button className="absolute top-4 right-4 text-white" onClick={() => setLb(-1)} aria-label="Close" data-testid="sch-gallery-lightbox-close"><X size={28} /></button>
            <figure onClick={(e) => e.stopPropagation()} className="max-w-5xl">
              <img src={visible[lb]?.image_url} alt={visible[lb]?.caption || ""} className="max-w-full max-h-[80vh] object-contain" />
              {visible[lb]?.caption && <figcaption className="mt-3 text-center text-white/90 text-sm">{visible[lb].caption}</figcaption>}
            </figure>
          </div>
        )}
      </section>
    </div>
  );
}


// ============================================================
// ============================================================
//  ACADEMICS PAGE (शैक्षणिक दस्तऐवज)
// ============================================================
export function SchoolAcademics() {
  const data = useSchool();
  const docs = data.academics || [];
  return (
    <div data-testid="sch-academics">
      <section className="bg-gradient-to-br from-[#8B1A1A] to-[#A52A2A] text-white py-14">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#FFC78A] font-bold">शैक्षणिक</div>
          <h1 className="mt-2 text-3xl sm:text-5xl font-extrabold" style={{ fontFamily: "'Tiro Devanagari Marathi', 'Noto Sans Devanagari', serif" }}>शैक्षणिक दस्तऐवज</h1>
          <p className="mt-4 max-w-2xl text-white/85">अभ्यासक्रम, वेळापत्रक, नियम — डाउनलोडसाठी उपलब्ध.</p>
        </div>
      </section>
      <PublicAcademicsPage docs={docs} theme="saffron" testidPrefix="sch-ac" />
    </div>
  );
}


// ============================================================
//  CONTACT PAGE (with inquiry form)
// ============================================================
export function SchoolContact() {
  const data = useSchool();
  const inst = data.institute || {};
  const depts = data.departments || [];
  const [form, setForm] = useState({ candidate_name: "", mobile: "", email: "", department_id: "", inquiry_details: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.candidate_name.trim()) return toast.error("कृपया नाव लिहा.");
    const mobile = form.mobile.replace(/\D/g, "");
    if (mobile.length !== 10) return toast.error("मोबाईल १० अंकी असावा.");
    setBusy(true);
    try {
      await api.post(`/public/admission-inquiry/${SLUG}`, {
        candidate_name: form.candidate_name.trim(), mobile, email: form.email.trim(),
        department_id: form.department_id || "", inquiry_details: form.inquiry_details.trim(),
      });
      toast.success("तुमची चौकशी पाठविली गेली. आम्ही लवकरच संपर्क साधू.");
      setForm({ candidate_name: "", mobile: "", email: "", department_id: "", inquiry_details: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "त्रुटी आली. कृपया पुन्हा प्रयत्न करा.");
    } finally { setBusy(false); }
  };
  const inputCls = "w-full border border-[#E8DCC0] rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#FF7B00] focus:border-[#FF7B00]";

  return (
    <div data-testid="sch-contact">
      <section className="bg-gradient-to-br from-[#8B1A1A] to-[#A52A2A] text-white py-14">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#FFC78A] font-bold">संपर्क व प्रवेश चौकशी</div>
          <h1 className="mt-2 text-3xl sm:text-5xl font-extrabold" style={{ fontFamily: "'Tiro Devanagari Marathi', 'Noto Sans Devanagari', serif" }}>संपर्क</h1>
          <p className="mt-3 max-w-2xl text-white/85">प्रवेशासाठी संपर्क करा. आम्ही लवकरच तुमच्याशी संवाद साधू.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-12 grid md:grid-cols-[1fr_1.3fr] gap-8">
        <div className="space-y-4">
          {(inst.address1 || inst.city) && (
            <div className="bg-white border border-[#F0E2C0] rounded-2xl p-5">
              <MapPin className="text-[#FF7B00]" size={24} />
              <div className="mt-3 font-extrabold text-[#8B1A1A]">{inst.name || "एम. आय. टी. तंत्रप्रशाला"}</div>
              <div className="mt-1 text-sm text-[#5b4a35]" data-testid="sch-contact-address">{[inst.address1, inst.address2, [inst.city, inst.pincode && `– ${inst.pincode}`].filter(Boolean).join(" "), inst.state].filter(Boolean).join(", ") || "सातारा परिसर, छत्रपती संभाजीनगर – ४३१०१०"}</div>
            </div>
          )}
          {inst.email && (
            <div className="bg-white border border-[#F0E2C0] rounded-2xl p-5">
              <Mail className="text-[#FF7B00]" size={24} />
              <div className="mt-3 font-extrabold text-[#8B1A1A]">ईमेल</div>
              <a href={`mailto:${inst.email}`} className="mt-1 block text-sm text-[#5b4a35] hover:text-[#FF7B00] break-all" data-testid="sch-contact-email">{inst.email}</a>
            </div>
          )}
          {(inst.contact_phone || inst.admission_phone) && (
            <div className="bg-white border border-[#F0E2C0] rounded-2xl p-5">
              <Phone className="text-[#FF7B00]" size={24} />
              <div className="mt-3 font-extrabold text-[#8B1A1A]">दूरध्वनी</div>
              <div className="mt-1 text-sm text-[#5b4a35]" data-testid="sch-contact-phone">{[inst.admission_phone, inst.contact_phone].filter(Boolean).join(" / ")}</div>
            </div>
          )}
        </div>

        <div>
          <div className="bg-white border-2 border-[#F0E2C0] rounded-2xl p-6 shadow-sm">
            <h2 className="text-2xl font-extrabold text-[#8B1A1A]" style={{ fontFamily: "'Tiro Devanagari Marathi', 'Noto Sans Devanagari', serif" }}>प्रवेश चौकशी फॉर्म</h2>
            <p className="mt-1 text-sm text-[#5b4a35]">तुमची माहिती भरा — आमचा कर्मचारी संपर्क साधेल.</p>
            <form onSubmit={submit} className="mt-5 grid sm:grid-cols-2 gap-4">
              <label className="block sm:col-span-2"><div className="text-xs font-bold uppercase tracking-wider text-[#8B1A1A] mb-1">विद्यार्थ्याचे संपूर्ण नाव *</div><input type="text" maxLength={100} value={form.candidate_name} onChange={(e) => set("candidate_name", e.target.value)} className={inputCls} required data-testid="sch-form-name" /></label>
              <label className="block"><div className="text-xs font-bold uppercase tracking-wider text-[#8B1A1A] mb-1">मोबाईल क्रमांक *</div><input type="tel" inputMode="numeric" pattern="[0-9]{10}" maxLength={10} value={form.mobile} onChange={(e) => set("mobile", e.target.value.replace(/\D/g, ""))} className={inputCls} placeholder="१० अंकी" required data-testid="sch-form-mobile" /></label>
              <label className="block"><div className="text-xs font-bold uppercase tracking-wider text-[#8B1A1A] mb-1">ईमेल (पर्यायी)</div><input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} data-testid="sch-form-email" /></label>
              <label className="block sm:col-span-2"><div className="text-xs font-bold uppercase tracking-wider text-[#8B1A1A] mb-1">इयत्ता / अभ्यासक्रम</div>
                <select value={form.department_id} onChange={(e) => set("department_id", e.target.value)} className={inputCls} data-testid="sch-form-course">
                  <option value="">— निवडा —</option>
                  {depts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </label>
              <label className="block sm:col-span-2"><div className="text-xs font-bold uppercase tracking-wider text-[#8B1A1A] mb-1">चौकशी तपशील</div><textarea rows={4} maxLength={500} value={form.inquiry_details} onChange={(e) => set("inquiry_details", e.target.value)} className={`${inputCls} resize-y`} placeholder="तुमची चौकशी इथे लिहा…" data-testid="sch-form-details" /></label>
              <button type="submit" disabled={busy} className="sm:col-span-2 inline-flex justify-center items-center gap-2 bg-[#FF7B00] hover:bg-[#E36B00] disabled:opacity-60 text-white font-bold px-6 py-3 rounded-md shadow-md" data-testid="sch-form-submit">{busy ? (<><Loader2 size={16} className="animate-spin" /> पाठवित आहे…</>) : (<><Send size={16} /> चौकशी पाठवा</>)}</button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}


// ============================================================
//  SCROLLER BAR — multi-item marquee with Saffron→Maroon palette
// ============================================================
function SchoolScrollerBar({ items }) {
  const list = (items || []).filter((s) => (s.text || "").trim());
  if (list.length === 0) return null;
  const renderSegment = (s, i) => {
    const inner = (
      <span className="inline-block whitespace-nowrap px-4">
        <span className="opacity-80 mr-2 align-middle">▸</span>
        {s.text}
      </span>
    );
    if (s.url) {
      return (
        <a
          key={`${s.id}-${i}`}
          href={s.url}
          target="_blank"
          rel="noopener noreferrer"
          className="hover:underline focus:underline outline-none"
          data-testid={`sch-scroller-link-${s.id}`}
        >
          {inner}
        </a>
      );
    }
    return (
      <span key={`${s.id}-${i}`} data-testid={`sch-scroller-text-${s.id}`}>
        {inner}
      </span>
    );
  };
  const duration = Math.max(20, Math.min(60, list.length * 14));
  return (
    <div
      className="bg-gradient-to-r from-[#FF7B00] to-[#8B1A1A] text-white border-y border-[#FF7B00]/40 overflow-hidden"
      data-testid="sch-scroller-bar"
    >
      <style>{`
        @keyframes sch-marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        [data-testid="sch-scroller-bar"]:hover [data-testid="sch-scroller-track"] {
          animation-play-state: paused !important;
        }
      `}</style>
      <div className="max-w-7xl mx-auto px-3 py-1.5 flex items-center gap-3">
        <span className="hidden sm:inline-flex text-[10px] tracking-[0.25em] uppercase font-bold bg-white/20 px-2 py-0.5 rounded-sm shrink-0">
          सूचना
        </span>
        <div className="flex-1 overflow-hidden">
          <span
            data-testid="sch-scroller-track"
            className="inline-block whitespace-nowrap text-sm font-medium"
            style={{ animation: `sch-marquee ${duration}s linear infinite` }}
          >
            {list.map(renderSegment)}
            {list.map((s, i) => renderSegment(s, `copy-${i}`))}
          </span>
        </div>
      </div>
    </div>
  );
}
